import React from 'react';
import TeacherStatusBadge from './TeacherStatusBadge';
import { API_BASE_URL } from '../../config.js';

const getFullAssetUrl = (url) => {
  if (!url) return '';
  if (url.startsWith('data:') || url.startsWith('http://') || url.startsWith('https://')) return url;
  const cleanUrl = url.startsWith('/') ? url : `/${url}`;
  return `${API_BASE_URL}${cleanUrl}`;
};

const TeacherProfileDrawer = ({ teacher, onClose, onEdit, onToggleStatus }) => {
  if (!teacher) return null;

  // Generate avatar initials from teacher name
  const getInitials = (name) => {
    if (!name) return 'TC';
    const parts = name.trim().split(' ');
    if (parts.length >= 2) {
      return (parts[0][0] + parts[1][0]).toUpperCase();
    }
    return name.slice(0, 2).toUpperCase();
  };

  const isActive = (teacher.status || 'ACTIVE').toUpperCase() === 'ACTIVE';

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(15, 23, 42, 0.4)',
        backdropFilter: 'blur(4px)',
        zIndex: 1100,
        display: 'flex',
        justifyContent: 'flex-end',
        animation: 'fadeIn 0.2s ease-out'
      }}
      onClick={onClose}
    >
      <div
        style={{
          width: '100%',
          maxWidth: '460px',
          height: '100%',
          backgroundColor: '#ffffff',
          boxShadow: '-10px 0 30px rgba(0, 0, 0, 0.15)',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          animation: 'slideInRight 0.25s ease-out'
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Drawer Header */}
        <div
          style={{
            padding: '24px 28px 20px 28px',
            borderBottom: '1px solid #f1f5f9',
            background: 'linear-gradient(180deg, #f8fafc 0%, #ffffff 100%)',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-start'
          }}
        >
          <div style={{ display: 'flex', gap: '16px', alignItems: 'center' }}>
            <div
              style={{
                width: '64px',
                height: '64px',
                borderRadius: '50%',
                background: 'linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%)',
                color: '#ffffff',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '22px',
                fontWeight: '700',
                boxShadow: '0 4px 12px rgba(37, 99, 235, 0.25)',
                border: '3px solid #ffffff',
                overflow: 'hidden',
                flexShrink: 0
              }}
            >
              {(teacher.photoUrl || teacher.photo) ? (
                <img
                  src={getFullAssetUrl(teacher.photoUrl || teacher.photo)}
                  alt={teacher.teacherName}
                  style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                  onError={(e) => {
                    e.currentTarget.style.display = 'none';
                    if (e.currentTarget.nextSibling) e.currentTarget.nextSibling.style.display = 'inline';
                  }}
                />
              ) : null}
              <span style={{ display: (teacher.photoUrl || teacher.photo) ? 'none' : 'inline' }}>
                {getInitials(teacher.teacherName)}
              </span>
            </div>

            <div>
              <h3 style={{ margin: 0, fontSize: '18px', fontWeight: '700', color: '#0f172a' }}>
                {teacher.teacherName}
              </h3>
              <div style={{ fontSize: '12px', fontFamily: 'monospace', color: '#2563eb', fontWeight: '600', marginTop: '2px' }}>
                ID: {teacher.teacherId}
              </div>
              <div style={{ marginTop: '6px' }}>
                <TeacherStatusBadge status={teacher.status} />
              </div>
            </div>
          </div>

          <button
            type="button"
            onClick={onClose}
            style={{
              background: '#f1f5f9',
              border: 'none',
              width: '32px',
              height: '32px',
              borderRadius: '8px',
              cursor: 'pointer',
              color: '#64748b',
              fontSize: '16px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              transition: 'all 0.2s ease'
            }}
            title="Close Drawer"
          >
            ✕
          </button>
        </div>

        {/* Drawer Body Scroll Content */}
        <div style={{ flex: 1, overflowY: 'auto', padding: '24px 28px' }}>
          {/* Section 1: Personal Information */}
          <div style={{ marginBottom: '24px' }}>
            <div style={{ fontSize: '11px', fontWeight: '700', color: '#94a3b8', letterSpacing: '0.05em', textTransform: 'uppercase', marginBottom: '12px' }}>
              👤 Personal Information
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', background: '#f8fafc', padding: '14px', borderRadius: '10px', border: '1px solid #f1f5f9' }}>
              <div>
                <div style={{ fontSize: '11px', color: '#64748b' }}>Date of Birth</div>
                <div style={{ fontSize: '13px', fontWeight: '600', color: '#1e293b', marginTop: '2px' }}>{teacher.dob || 'Not provided'}</div>
              </div>
              <div>
                <div style={{ fontSize: '11px', color: '#64748b' }}>Age</div>
                <div style={{ fontSize: '13px', fontWeight: '600', color: '#1e293b', marginTop: '2px' }}>{teacher.age ? `${teacher.age} years` : 'N/A'}</div>
              </div>
              <div style={{ gridColumn: 'span 2' }}>
                <div style={{ fontSize: '11px', color: '#64748b' }}>Address</div>
                <div style={{ fontSize: '13px', fontWeight: '600', color: '#1e293b', marginTop: '2px' }}>{teacher.address || 'Not provided'}</div>
              </div>
            </div>
          </div>

          {/* Section 2: Academic Profile */}
          <div style={{ marginBottom: '24px' }}>
            <div style={{ fontSize: '11px', fontWeight: '700', color: '#94a3b8', letterSpacing: '0.05em', textTransform: 'uppercase', marginBottom: '12px' }}>
              🎓 Academic Profile
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', background: '#f8fafc', padding: '14px', borderRadius: '10px', border: '1px solid #f1f5f9' }}>
              <div>
                <div style={{ fontSize: '11px', color: '#64748b' }}>Highest Degree</div>
                <div style={{ fontSize: '13px', fontWeight: '600', color: '#1e293b', marginTop: '2px' }}>{teacher.degree || 'N/A'}</div>
              </div>
              <div>
                <div style={{ fontSize: '11px', color: '#64748b' }}>Qualifications</div>
                <div style={{ fontSize: '13px', fontWeight: '600', color: '#1e293b', marginTop: '2px' }}>{teacher.qualifications || 'N/A'}</div>
              </div>
              <div>
                <div style={{ fontSize: '11px', color: '#64748b' }}>Specialization</div>
                <div style={{ fontSize: '13px', fontWeight: '600', color: '#2563eb', marginTop: '2px' }}>{teacher.specialization || 'General'}</div>
              </div>
              <div>
                <div style={{ fontSize: '11px', color: '#64748b' }}>Teaching Experience</div>
                <div style={{ fontSize: '13px', fontWeight: '600', color: '#1e293b', marginTop: '2px' }}>{teacher.experience || 0} Years</div>
              </div>
            </div>
          </div>

          {/* Section 3: Employment Details */}
          <div style={{ marginBottom: '24px' }}>
            <div style={{ fontSize: '11px', fontWeight: '700', color: '#94a3b8', letterSpacing: '0.05em', textTransform: 'uppercase', marginBottom: '12px' }}>
              💼 Employment Details
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', background: '#f8fafc', padding: '14px', borderRadius: '10px', border: '1px solid #f1f5f9' }}>
              <div>
                <div style={{ fontSize: '11px', color: '#64748b' }}>Designation / Role</div>
                <div style={{ fontSize: '13px', fontWeight: '600', color: '#0f172a', marginTop: '2px' }}>{teacher.designation || 'Faculty Member'}</div>
              </div>
              <div>
                <div style={{ fontSize: '11px', color: '#64748b' }}>Employment Status</div>
                <div style={{ marginTop: '2px' }}>
                  <TeacherStatusBadge status={teacher.status} />
                </div>
              </div>
            </div>
          </div>

          {/* Section 4: Contact Details */}
          <div>
            <div style={{ fontSize: '11px', fontWeight: '700', color: '#94a3b8', letterSpacing: '0.05em', textTransform: 'uppercase', marginBottom: '12px' }}>
              📞 Contact Information
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: '12px', background: '#f8fafc', padding: '14px', borderRadius: '10px', border: '1px solid #f1f5f9' }}>
              <div>
                <div style={{ fontSize: '11px', color: '#64748b' }}>Mobile Phone</div>
                <div style={{ fontSize: '13px', fontWeight: '600', color: '#1e293b', marginTop: '2px' }}>{teacher.phoneNumber || 'Not provided'}</div>
              </div>
              <div>
                <div style={{ fontSize: '11px', color: '#64748b' }}>Email Address</div>
                <div style={{ fontSize: '13px', fontWeight: '600', color: '#1e293b', marginTop: '2px' }}>{teacher.gmail || 'Not provided'}</div>
              </div>
            </div>
          </div>
        </div>

        {/* Drawer Sticky Footer Actions */}
        <div
          style={{
            padding: '16px 28px',
            borderTop: '1px solid #f1f5f9',
            backgroundColor: '#ffffff',
            display: 'flex',
            gap: '12px'
          }}
        >
          <button
            type="button"
            className="fs-btn-secondary"
            onClick={() => {
              onClose();
              onEdit(teacher);
            }}
            style={{ flex: 1, justifyContent: 'center' }}
          >
            ✏️ Edit Teacher
          </button>

          <button
            type="button"
            className="fs-btn-primary"
            onClick={() => {
              onToggleStatus(teacher);
            }}
            style={{
              flex: 1,
              justifyContent: 'center',
              backgroundColor: isActive ? '#f59e0b' : '#16a34a',
              borderColor: isActive ? '#d97706' : '#16a34a'
            }}
          >
            {isActive ? '⏸️ Deactivate' : '▶️ Activate'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default TeacherProfileDrawer;
